import React from 'react';
import { styled } from '@linaria/react';

import { Key } from './Synthesizer.constants';

interface Props {
  musicalKey: Key;
  isPressed: boolean;
  isPreviousKeyPressed: boolean;
  isNextKeyPressed: boolean;
  handleClickKey: (key: string) => void;
  handleReleaseKey: (key: string) => void;
  isGlissando: boolean;
  setIsGlissando: React.Dispatch<React.SetStateAction<boolean>>;
  layers: number;
}

function KeyBlack({
  musicalKey,
  isPressed,
  isPreviousKeyPressed,
  isNextKeyPressed,
  handleClickKey,
  handleReleaseKey,
  isGlissando,
  setIsGlissando,
  layers,
}: Props) {
  const handleMouseDown = React.useCallback(() => {
    setIsGlissando(true);
    handleClickKey(musicalKey);
  }, [handleClickKey, musicalKey, setIsGlissando]);

  const handleMouseUp = React.useCallback(() => {
    setIsGlissando(false);
    handleReleaseKey(musicalKey);
  }, [handleReleaseKey, musicalKey, setIsGlissando]);

  const handleMouseLeave = React.useCallback(() => {
    if (isPressed) {
      handleReleaseKey(musicalKey);
    }
  }, [handleReleaseKey, musicalKey, isPressed]);

  const handleMouseEnter = React.useCallback(
    (e: React.MouseEvent) => {
      // Primary button (usually left) is pressed
      if (e.buttons === 1) {
        handleClickKey(musicalKey);
      }
    },
    [handleClickKey, musicalKey]
  );

  // Handle case where mouse is released outside the window
  React.useEffect(() => {
    if (!isGlissando) return;

    const handleWindowMouseUp = () => {
      setIsGlissando(false);
      if (isPressed) {
        handleReleaseKey(musicalKey);
      }
    };

    window.addEventListener('mouseup', handleWindowMouseUp);
    return () => {
      window.removeEventListener('mouseup', handleWindowMouseUp);
    };
  }, [
    isGlissando,
    isPressed,
    handleReleaseKey,
    musicalKey,
    setIsGlissando,
  ]);

  return (
    <OuterWrapper>
      <Wrapper
        data-is-pressed={isPressed}
        data-is-next-key-pressed={isNextKeyPressed}
        data-is-previous-key-pressed={isPreviousKeyPressed}
        data-hide-rounding={layers < 7}
        data-hide-edge={layers < 7}
        data-hide-light-glare={layers < 8}
        data-hide-texture={layers < 5}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onMouseEnter={handleMouseEnter}
      >
        <KeyFace />
        <KeyEdge />
      </Wrapper>
    </OuterWrapper>
  );
}

const OuterWrapper = styled.div`
  position: relative;
  z-index: 2;
  width: 0px;
  height: 100%;
`;

const Wrapper = styled.div`
  position: relative;
  width: 16px;
  height: 60%;
  background: black;
  padding: 1px;
  border-radius: 2px 2px 4px 4px;
  transform: translate(-50%, -3px);
  perspective: 100px;
  overflow: hidden;
  overflow: clip;

  &[data-hide-rounding='true'] {
    border-radius: 0;
  }
`;

const KeyFace = styled.div`
  position: absolute;
  top: 0;
  left: 2px;
  right: 2px;
  bottom: 7px;
  background: linear-gradient(
    to bottom,
    hsl(0deg 0% 30%),
    hsl(0deg 0% 20%)
  );
  border-top: 2px solid black;
  box-shadow: inset 0px 0px 0.5px 1px hsl(0deg 0% 100% / 0.1);
  border-radius: 2px 2px 4px 4px;
  overflow: hidden;
  overflow: clip;
  transform: rotateX(-10deg);
  transform-origin: top center;

  ${Wrapper}[data-hide-texture='true'] & {
    background: black;
    box-shadow: none;
  }

  /* Lil' light glare on the left edge */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 1px;
    height: 100%;
    background: linear-gradient(
      to bottom,
      hsl(0deg 0% 100% / 0.1),
      hsl(0deg 0% 100% / 0.3) 20%,
      hsl(0deg 0% 100% / 0.5) 25%,
      hsl(0deg 0% 100% / 0.3) 40%,
      hsl(0deg 0% 100% / 0.1)
    );
  }

  ${Wrapper}[data-hide-light-glare='true'] &::before {
    display: none;
  }

  ${Wrapper}[data-is-pressed='true'] & {
    bottom: 3px;
    background: linear-gradient(
      to bottom,
      hsl(0deg 0% 20%),
      hsl(0deg 0% 5%)
    );

    /* When pressed, the light glare along the left moves down, and we get a new light glare along the bottom */
    &::before {
      background: linear-gradient(
        to bottom,
        hsl(0deg 0% 100% / 0) 0% 30%,
        hsl(0deg 0% 100% / 0.3) 50%,
        hsl(0deg 0% 100% / 0.5) 90%,
        hsl(0deg 0% 100% / 0) 100%
      );
    }

    &::after {
      content: '';
      position: absolute;
      bottom: 0px;
      width: 100%;
      height: 1px;
      background: linear-gradient(
        to right,
        hsl(0deg 0% 100% / 0.2),
        hsl(0deg 0% 100% / 0.5),
        hsl(0deg 0% 100% / 0)
      );
    }
  }
`;

const KeyEdge = styled.div`
  position: absolute;
  bottom: 1px;
  left: 1px;
  right: 1px;
  height: 7px;
  border-radius: 3px;
  perspective: 100px;
  overflow: clip;

  /* Reflection of the left key underneath */
  &::before,
  &::after {
    content: '';
    position: absolute;
    bottom: -1px;
    width: calc(50% - 0.5px);
    height: 32px;
    background: linear-gradient(
      to top,
      hsl(0deg 0% 100% / 0.5),
      hsl(0deg 0% 100% / 0.15)
    );
    transform: rotateX(72deg);
    transform-origin: bottom center;
  }

  &::before {
    left: 0;
  }
  &::after {
    right: 0;
  }

  ${Wrapper}[data-hide-edge='true'] & {
    &::before,
    &::after {
      display: none;
    }
  }

  ${Wrapper}[data-is-next-key-pressed='true'] &::after {
    opacity: 0.666;
  }
  ${Wrapper}[data-is-previous-key-pressed='true'] &::before {
    opacity: 0.666;
  }

  ${Wrapper}[data-is-pressed='true'] & {
    height: 3px;
    border-radius: 6px;

    &::before,
    &::after {
      opacity: 0.7;
    }
  }

  ${Wrapper}[data-is-pressed='true'][data-is-next-key-pressed='true'] &::after {
    opacity: 0.3;
  }
  ${Wrapper}[data-is-pressed='true'][data-is-previous-key-pressed='true'] &::before {
    opacity: 0.3;
  }
`;

export default React.memo(KeyBlack);
